import {useInfiniteQuery, useQuery} from '@tanstack/react-query'

import {type Classroom} from '#/state/classrooms/types'
import {STALE} from '#/state/queries'
import {useAgent, useSession} from '#/state/session'
import {classroomQuery} from './client'
import {classroomKeys} from './keys'
import {type ClassroomViewJson, toClassroom} from './map'

const NS = 'app.taderok.classroom'
const PAGE_SIZE = 25

type ClassroomListJson = {classrooms: ClassroomViewJson[]; cursor?: string}

/** Public classrooms matching `q`. The server rejects a blank `q`, so one never asks. */
export function useSearchClassroomsQuery(q: string, enabled: boolean) {
  const agent = useAgent()
  const {hasSession} = useSession()
  const term = q.trim()

  return useInfiniteQuery({
    queryKey: classroomKeys.search(term),
    queryFn: async ({pageParam}) => {
      const res = await classroomQuery<ClassroomListJson>(
        agent,
        `${NS}.searchClassrooms`,
        {q: term, limit: PAGE_SIZE, cursor: pageParam},
      )
      return {
        classrooms: res.classrooms.map(toClassroom) as Classroom[],
        cursor: res.cursor,
      }
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: page => page.cursor,
    staleTime: STALE.MINUTES.ONE,
    enabled: enabled && hasSession && term.length > 0,
  })
}

export function useSuggestedClassroomsQuery() {
  const agent = useAgent()
  const {hasSession} = useSession()

  return useQuery({
    queryKey: classroomKeys.suggested(),
    queryFn: async () => {
      const res = await classroomQuery<{classrooms: ClassroomViewJson[]}>(
        agent,
        `${NS}.listSuggestedClassrooms`,
        {limit: 20},
      )
      return res.classrooms.map(toClassroom)
    },
    staleTime: STALE.MINUTES.FIVE,
    enabled: hasSession,
  })
}
